import { useCallback } from 'react'
import { useAuth } from './useAuth'
import type { AdminRole } from '../types'

/**
 * Permission checks for the signed-in admin.
 *   can('shops.suspend')            — one "<menu>.<action>" permission
 *   canAny('billing.view', …)       — any of them
 * super_admin can do everything. The server checks every request as well —
 * this only hides buttons/menus the admin can't use.
 */
export const usePermission = () => {
  const { currentAdmin } = useAuth()
  const isSuperAdmin = currentAdmin?.role === 'super_admin'

  const can = useCallback(
    (permission: string): boolean => {
      if (!currentAdmin) return false
      if (currentAdmin.role === 'super_admin') return true
      return (currentAdmin.permissions ?? []).includes(permission)
    },
    [currentAdmin],
  )
  const canAny = useCallback((...permissions: string[]) => permissions.some(can), [can])

  /** @deprecated role checks — use can() */
  const canAccess = (requiredRoles: AdminRole[]): boolean =>
    !!currentAdmin && requiredRoles.includes(currentAdmin.role as AdminRole)

  return {
    can,
    canAny,
    canAccess,
    isSuperAdmin,
    isSupport: currentAdmin?.role === 'support',
    isFinance: currentAdmin?.role === 'finance',
    isProductOps: currentAdmin?.role === 'product_ops',
  }
}

/** Menu/page keys in the order of the permission catalog (longest first for path matching). */
export const PAGE_KEYS = [
  'master-data/business-types',
  'master-data/payment-methods',
  'subscription-promotions',
  'referrers',
  'consents',
  'announcements',
  'usage-analytics',
  'business-insights',
  'finance-reports',
  'system-settings',
  'integrations',
  'system-health',
  'shop-payments',
  'feature-flags',
  'menu-control',
  'subscriptions',
  'notifications',
  'admin-users',
  'audit-logs',
  'money-logs',
  'ai-usage',
  'customer-analytics',
  'dashboard',
  'billing',
  'support',
  'shops',
  'plans',
]

/** The permission needed to open a path (null = every signed-in admin). */
export function permissionForPath(pathname: string): string | null {
  const p = pathname.replace(/^\/+/, '')
  if (p === '' || p.startsWith('account') || p.startsWith('dashboard')) return null
  if (p.startsWith('permissions')) return 'admin-users.permissions'
  const key = PAGE_KEYS.find((k) => p === k || p.startsWith(k + '/'))
  return key ? `${key}.view` : null
}
